import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Message, MessageService } from 'primeng/api';
import { CalendarModule } from 'primeng/calendar';
import { CardModule } from 'primeng/card';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';
import { ProgressLoaderService } from 'src/app/demo/service/progress-loader.service';
import { UserService } from 'src/app/demo/service/user.service';
import { createNewUser } from 'src/app/demo/utilities/user-utils';
import { MultiSelectModule } from 'primeng/multiselect';
import { RolesService } from 'src/app/demo/service/roles.service';
import { Router } from '@angular/router';
import { NgxTranslateModule } from 'src/app/translate.module';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from 'src/app/demo/service/language.service';
import { languages } from 'src/app/demo/constants/languages';
import { showErrorToast } from 'src/app/demo/utilities/toast-utils';

interface GroupOption {
  name: string;
  value: string;
}

@Component({
  selector: 'app-register-user',
  standalone: true,
  imports: [CommonModule, CalendarModule, NgxTranslateModule, InputTextModule, CardModule, MessagesModule, DropdownModule, FormsModule, ReactiveFormsModule, MultiSelectModule],
  templateUrl: './register-user.component.html',
  styleUrls: ['./register-user.component.scss'],
  providers: [MessagesModule]
})

export class RegisterUserComponent implements OnInit {

  userForm!: FormGroup;
  messages!: Message[];
  availableUserGroups: GroupOption[] = [];

  languages: any[] = languages;
  selectedLanguage!: string;

  constructor(private fb: FormBuilder, private translateService: TranslateService, private messageService: MessageService, private languageService: LanguageService, private router: Router, private progressLoader: ProgressLoaderService, private userService: UserService, private rolesService: RolesService) { }

  ngOnInit(): void {
    this.initializeUserForm()
    this.fetchUserGroups();
    this.initializeLocale();
  }

  initializeLocale() {
    this.languageService.currentLanguage$.subscribe((language) => {
      this.selectedLanguage = language;
      this.setLanguage(this.selectedLanguage);
    });
  }

  setLanguage(language: string) {
    this.translateService.use(language);
    this.languageService.setLanguage(language);
  }

  initializeUserForm() {
    this.userForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      repeatPassword: ['', [Validators.required]],
      password: ['', [Validators.required]],
      group: ['', [Validators.required]]
    })
  }

  clearForm() {
    this.userForm.reset();
  }

  back() {
    this.router.navigate(['/home/users']);
  }

  fetchUserGroups() {
    this.rolesService.fetchUserGroups().subscribe({
      next: value => {
        value.forEach(item => {
          this.translateService.get(item.toString()).subscribe((value) => {
            this.availableUserGroups.push({
              name: value,
              value: item.toString()
            })
          })
        })
      },
      error: err => {
        showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService,this.translateService)
      }
    })
  }

  registerUserClicked() {
    this.progressLoader.show()
    this.userService.createUser(createNewUser(this.userForm)).subscribe({
      next: value => {
        this.progressLoader.hide();
        this.showSuccessToast("User created successfully");
      },
      error: err => {
        this.progressLoader.hide();
        showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService,this.translateService)
      }
    })
  }

  showSuccessToast(message: string) {
    this.translateService.get(['Success', message]).subscribe(translations => {
      this.messageService.add({
        severity: 'success',
        summary: translations['Success'],
        life: 2000,
        detail: translations.get(message),
        key: 'tc',
      });
    })
  }

  showErrorToast(error: string) {
    this.translateService.get(['Error', error]).subscribe(translations => {
      this.messageService.add({
        severity: 'error',
        summary: translations['Error'],
        life: 2000,
        detail: translations.get(error),
        key: 'tc',
      });
    })
  }
}
